import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="bg-white border-b sticky top-0 z-10 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-gray-100">
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-2">
          <Shield size={20} className="text-blue-600" />
          <h1 className="font-bold text-lg">Privacy Policy</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6 text-sm text-gray-700">
        <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
          <p className="text-xs text-gray-500">Last updated: April 1, 2026</p>
          <p className="text-xs text-gray-500 mt-0.5">Effective date: April 1, 2026</p>
        </div>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">1. About CampusSwap</h2>
          <p>CampusSwap ("we", "us", "our", or "the Platform") is a peer-to-peer student marketplace operated by CampusSwap (India). Our platform enables college students and parents to buy, sell, swap, and exchange academic and everyday items within their campus community.</p>
          <p className="mt-2">This Privacy Policy is published in accordance with:</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>Rule 4 of the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021</li>
            <li>The Digital Personal Data Protection Act, 2023 (DPDP Act)</li>
            <li>The Information Technology Act, 2000 and rules made thereunder</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">2. Information We Collect</h2>
          <p className="mb-2">We collect only the minimum information necessary to provide our services:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Identity data:</strong> Full name, email address, phone number, college/school name</li>
            <li><strong>Verification data:</strong> College ID card photo (for student verification only)</li>
            <li><strong>Profile data:</strong> Profile photo, user type (student/parent), year of study</li>
            <li><strong>Listing data:</strong> Item photos, descriptions, prices, category, condition</li>
            <li><strong>Communication data:</strong> Messages between buyers and sellers on the platform</li>
            <li><strong>Transaction data:</strong> Records of items bought, sold, or swapped</li>
            <li><strong>Usage data:</strong> Pages visited, features used, last active timestamp (for platform safety)</li>
            <li><strong>Device data:</strong> Browser type, operating system (collected automatically by Firebase)</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">3. How We Use Your Information</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>To create, manage, and secure your account</li>
            <li>To verify your student or parent identity</li>
            <li>To display your listings to relevant buyers on the platform</li>
            <li>To facilitate communication between buyers and sellers</li>
            <li>To send platform notifications, alerts, and updates</li>
            <li>To detect, prevent, and respond to fraud, spam, and abuse</li>
            <li>To enforce our Terms &amp; Conditions and community guidelines</li>
            <li>To improve platform features and fix technical issues</li>
            <li>To comply with applicable laws and legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">4. Legal Basis for Processing (DPDP Act 2023)</h2>
          <p className="mb-2">We process your personal data on the following legal bases:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Consent:</strong> You provide consent when you create an account and accept these policies</li>
            <li><strong>Contractual necessity:</strong> To fulfil the services you request (posting listings, chats, etc.)</li>
            <li><strong>Legitimate interest:</strong> To maintain platform safety, prevent fraud, and improve our services</li>
            <li><strong>Legal obligation:</strong> To comply with Indian law when required by courts or government authorities</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">5. Information Sharing</h2>
          <p className="mb-2 font-medium text-red-600">We do NOT sell, rent, or trade your personal data to any third party.</p>
          <p className="mb-2">Your information may be shared only in these limited circumstances:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Other users:</strong> Your name, college, and listing details are visible to other platform users. Your phone number is only shared when you initiate WhatsApp contact.</li>
            <li><strong>Platform admins:</strong> Admins can view account details strictly for moderation and verification purposes.</li>
            <li><strong>Firebase (Google):</strong> We use Google Firebase for authentication, database, and storage. Your data is stored on Firebase servers governed by Google's Privacy Policy.</li>
            <li><strong>Cloudinary:</strong> Images you upload are stored on Cloudinary's servers.</li>
            <li><strong>Law enforcement:</strong> We may disclose information if required by law, court order, or government authority under the IT Act, 2000.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">6. Data Retention</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Account data is retained as long as your account is active</li>
            <li>Listing data is retained for 2 years after the listing is closed</li>
            <li>Chat messages are retained for 1 year</li>
            <li>College ID verification photos are deleted within 30 days of verification approval or rejection</li>
            <li>On account deletion, all your personal data is permanently removed within 30 days</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">7. Data Security</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>All data is stored on Google Firebase with industry-standard encryption</li>
            <li>Passwords are hashed and never stored in plain text</li>
            <li>Platform access is protected by Firebase security rules</li>
            <li>HTTPS is enforced on all connections (HSTS enabled)</li>
            <li>Admin access is protected by multi-factor authentication</li>
            <li>We follow reasonable security practices as required under Section 43A of the IT Act, 2000</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">8. Your Rights (DPDP Act 2023)</h2>
          <p className="mb-2">As a data principal under the DPDP Act, 2023, you have the following rights:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Right to access:</strong> Request a copy of the personal data we hold about you</li>
            <li><strong>Right to correction:</strong> Update your profile information anytime from the Profile page</li>
            <li><strong>Right to erasure:</strong> Delete your account and all associated data from the Profile → Delete Account section</li>
            <li><strong>Right to withdraw consent:</strong> You may withdraw consent at any time by deleting your account</li>
            <li><strong>Right to grievance redressal:</strong> Contact our Grievance Officer (see Section 11)</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">9. Cookies and Tracking</h2>
          <p>We use Firebase, which may set cookies or use local storage for authentication and session management. These are essential for the app to function. We do not use advertising or tracking cookies.</p>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">10. Children's Privacy</h2>
          <p>CampusSwap is intended for users aged 16 and above. We do not knowingly collect personal data from children under 16. If you believe a child under 16 has created an account, please contact our Grievance Officer immediately for account removal.</p>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">11. Grievance Officer</h2>
          <p className="mb-2">In accordance with the Information Technology Act, 2000 and rules made thereunder, and the DPDP Act 2023, we have appointed a Grievance Officer:</p>
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 space-y-1">
            <p><strong>Name:</strong> CampusSwap Support Team</p>
            <p><strong>Designation:</strong> Grievance Officer</p>
            <p><strong>Email:</strong> <a href="mailto:campusswap.official@gmail.com" className="text-blue-600">campusswap.official@gmail.com</a></p>
            <p><strong>Response time:</strong> Within 48 hours of receipt</p>
            <p><strong>Resolution time:</strong> Within 30 days of receipt</p>
          </div>
          <p className="mt-2 text-xs text-gray-500">You may file a complaint or report any privacy concern by emailing the Grievance Officer with your name, contact details, and a description of your complaint.</p>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">12. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. Material changes will be notified through the app. Your continued use of the platform after changes constitutes acceptance of the updated policy.</p>
        </section>

        <section>
          <h2 className="font-bold text-base text-gray-900 mb-2">13. Contact Us</h2>
          <p>For any privacy-related questions, data requests, or concerns:</p>
          <div className="mt-2 space-y-1">
            <p><strong>Email:</strong> <a href="mailto:campusswap.official@gmail.com" className="text-blue-600">campusswap.official@gmail.com</a></p>
            <p><strong>Platform:</strong> Profile → Help &amp; Support</p>
          </div>
        </section>
      </div>
    </div>
  );
}
